import { Play, UserRound } from "lucide-react";

import { cn } from "@/shared/lib/utils";
import type { CharacterDto } from "@/entities/character/api/characterService";

/**
 * 人物主素材缩略(0916 归一补洞;人物库墙 / 选人弹窗 / 真人出镜选人卡三处共用)。
 *
 * <p>★为什么必须有这么一个共用件:归一后「只有视频」的人物把那条 mp4 钉成主素材,
 * 而**视频没有位图缩略图**(`thumbKey` 恒 null)—— 三处消费面当时都写着
 * `<img src={primaryThumbUrl ?? primaryUrl}>`,拿到 mp4 就是一张 naturalWidth=0 的裂图。
 * 而「只有视频的人物」恰恰是这次归一要救的那一类(老板 0915 实报「只有视频他无法上传」)。
 * 同型漏 0916 在素材网格上已经犯过一次(页面 0 个 video、1 个裂 img),这里一次收干净:
 * 三处都走这一个组件,下一处新消费面也照抄它,不再各写各的 `??` 链。
 */
export function CharacterThumb({
  character: c,
  /**
   * `contain` = 定框 + 模糊铺底吃留白、前景整图可见(库墙大卡:0908 用户「分辨率不同显示会乱」);
   * `cover` = 直接铺满(小头像/密集网格,裁一点无所谓)。
   */
  fit = "cover",
  iconClassName = "h-8 w-8",
  emptyHint,
}: {
  character: Pick<CharacterDto, "name" | "primaryUrl" | "primaryThumbUrl" | "primaryKind">;
  fit?: "cover" | "contain";
  iconClassName?: string;
  /** 无素材时占位里的一句话(库墙大卡给,小头像不给 —— 那里放不下也不需要)。 */
  emptyHint?: string;
}) {
  const src = c.primaryThumbUrl ?? c.primaryUrl;
  if (!src) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-2 text-muted-foreground">
        <UserRound className={cn("opacity-40", iconClassName)} />
        {emptyHint && <span className="text-xs">{emptyHint}</span>}
      </div>
    );
  }
  if (c.primaryKind === "video") {
    return (
      <div className="relative h-full w-full">
        {/* preload=metadata:只拉头部出首帧,不拉整段 —— 一屏几十张卡不能每张下一个视频。
            muted + playsInline 是移动端能显示首帧的前提(iOS 上少一个就是一块黑)。 */}
        <video
          src={src}
          muted
          playsInline
          preload="metadata"
          className="h-full w-full object-cover"
          aria-label={`${c.name} 主素材(视频)`}
        >
          {/* 缩略预览无字幕轨(a11y 规则占位)。 */}
          <track kind="captions" />
        </video>
        <span className="pointer-events-none absolute inset-0 flex items-center justify-center">
          <span className="grid h-7 w-7 place-items-center rounded-full bg-black/45 text-white">
            <Play className="h-3.5 w-3.5 translate-x-[1px]" />
          </span>
        </span>
      </div>
    );
  }
  if (fit === "contain") {
    return (
      <>
        <img
          src={src}
          alt=""
          aria-hidden
          className="absolute inset-0 h-full w-full scale-110 object-cover opacity-40 blur-xl"
        />
        <img
          src={src}
          alt={`${c.name} 主素材`}
          className="relative z-10 h-full w-full object-contain"
        />
      </>
    );
  }
  return <img src={src} alt={`${c.name} 主素材`} className="h-full w-full object-cover" />;
}
